import React, { useCallback, useEffect, useState } from 'react';
import useQuranReader from '../hooks/useQuranReader';
import { downloadPack, loadPack, deletePack, setCachedPack } from '../utils/offlineQuran';
import { storageEstimate, requestPersistence, formatBytes, isSupported } from '../utils/offlineStore';

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (error) {
    return '';
  }
};

/**
 * Download the whole text of the Quran for reading without a connection.
 *
 * Text only — a few megabytes. Recitation audio is hundreds of megabytes and is
 * deliberately not part of this.
 */
const OfflineSettings = () => {
  const { selectedTranslation, availableEditions } = useQuranReader();

  const [pack, setPack] = useState(null);
  const [checking, setChecking] = useState(true);
  const [progress, setProgress] = useState(null);
  const [message, setMessage] = useState(null);
  const [storage, setStorage] = useState(null);

  const refresh = useCallback(async () => {
    const [stored, estimate] = await Promise.all([loadPack(), storageEstimate()]);
    setPack(stored);
    setCachedPack(stored);
    setStorage(estimate);
    setChecking(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const editionName = (identifier) => {
    const match = availableEditions.translations.find((entry) => entry.identifier === identifier);
    return match ? match.englishName : identifier;
  };

  const download = async () => {
    setMessage(null);
    setProgress({ step: 0, total: 3, label: 'Starting' });
    try {
      // Ask the browser to keep this rather than evict it under pressure
      await requestPersistence();
      const stored = await downloadPack({
        translation: selectedTranslation,
        onProgress: setProgress
      });
      setCachedPack(stored);
      setPack(stored);
      setMessage('The Quran is now available without a connection.');
      setStorage(await storageEstimate());
    } catch (error) {
      setMessage(`Download failed: ${error.message}. Nothing was saved.`);
    } finally {
      setProgress(null);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove the downloaded text? Reading will need a connection again.')) return;
    await deletePack();
    setCachedPack(null);
    setPack(null);
    setMessage('Downloaded text removed.');
    setStorage(await storageEstimate());
  };

  if (!isSupported()) {
    return (
      <p className="text-sm font-lato" style={{ color: 'var(--color-charcoal)', opacity: 0.75 }}>
        This browser cannot store the Quran for offline reading.
      </p>
    );
  }

  const staleTranslation = pack && pack.editions.translation !== selectedTranslation;

  return (
    <div>
      <p className="text-sm mb-3 font-lato" style={{ color: 'var(--color-charcoal)', opacity: 0.75 }}>
        Keep the full text — Arabic, translation and transliteration — on this device, so
        reading works with no connection. A few megabytes. Recitation audio is not included:
        that would run to hundreds.
      </p>

      {checking ? (
        <p className="text-sm font-lato" style={{ color: 'var(--color-charcoal)', opacity: 0.6 }}>Checking…</p>
      ) : (
        <>
          {pack ? (
            <div
              className="p-3 rounded-md mb-3 text-sm font-lato"
              style={{ backgroundColor: 'var(--color-light-gray)', color: 'var(--color-charcoal)' }}
            >
              <p className="font-medium text-heading">Downloaded · {formatBytes(pack.bytes)}</p>
              <p className="opacity-75">
                {editionName(pack.editions.translation)} · {formatDate(pack.downloadedAt)}
              </p>
              {staleTranslation && (
                <p className="mt-2" style={{ color: 'var(--color-danger)' }}>
                  You now read {editionName(selectedTranslation)}. Download again to have that
                  translation available offline.
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm mb-3 font-lato" style={{ color: 'var(--color-charcoal)', opacity: 0.6 }}>
              Not downloaded.
            </p>
          )}

          {progress && (
            <div className="mb-3">
              <div className="w-full rounded-full h-2" style={{ backgroundColor: 'var(--color-light-gray)' }}>
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${(progress.step / progress.total) * 100}%`,
                    backgroundColor: 'var(--color-medium-green)'
                  }}
                />
              </div>
              <p className="text-sm mt-1 font-lato" style={{ color: 'var(--color-charcoal)' }}>
                {progress.label} ({progress.step} of {progress.total})
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={download}
              disabled={Boolean(progress)}
              className="px-4 py-2 rounded-md bg-button-bg text-button-text hover:bg-medium-green font-lato transition-colors disabled:opacity-50"
            >
              {progress ? 'Downloading…' : pack ? 'Download again' : 'Download for offline'}
            </button>
            {pack && !progress && (
              <button
                type="button"
                onClick={remove}
                className="px-4 py-2 rounded-md font-lato transition-colors"
                style={{ backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}
              >
                Remove
              </button>
            )}
          </div>

          {storage && storage.quota > 0 && (
            <p className="text-xs mt-3 font-lato" style={{ color: 'var(--color-charcoal)', opacity: 0.6 }}>
              {formatBytes(storage.usage)} used of {formatBytes(storage.quota)} this site may store.
            </p>
          )}

          {message && (
            <p className="text-sm mt-3 font-lato" role="status" style={{ color: 'var(--color-charcoal)' }}>
              {message}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default OfflineSettings;
